/**
 * D17c: the CUSTOMER quote-send. The recipient is the SERVER-OF-RECORD contact
 * detail (booking detail customer.phone_e164 / customer.email) displayed as
 * microcopy — no editable recipient field exists here, and the POST body
 * carries DECISION FLAGS only (SEC-6). Per-send deposit choice (Q19: Helen
 * decides per job). Delivery state renders the TRUE ledger status via the G3
 * registry labels, never an optimistic 'sent' (SEC-7). Failures speak human
 * (§7.3).
 *
 * T549 M7 — the channel. Three things change and one thing deliberately does
 * not:
 *  1. the delivery status says WHICH channel it belongs to. `latestSend.channel`
 *     has ridden into this component since M6 and was never rendered, so "Sent"
 *     was silently attributed to whatever Helen assumed she had used;
 *  2. Helen can pick the channel per send, defaulting to what the customer
 *     ASKED FOR (contact_preference), which is NULL on every call-in and
 *     walk-in booking and must read as the normal case, not as an error;
 *  3. the send affordance is gated on the SELECTED channel having a recipient,
 *     not on a phone existing — an email-only customer can now be quoted.
 * What does not change: the override is component state for THIS send. Nothing
 * here writes it back. The customer's stated preference stays the durable record
 * of what they asked for, and the send_ledger row is the durable record of what
 * actually went out.
 */
import { useState } from 'preact/hooks';
import {
  IMPLEMENTED_CHANNELS,
  CHANNEL_EMAIL,
  CHANNEL_SMS,
  contactPreferenceLabel,
  sendOutcome,
  type Channel,
} from '../../shared/send-schema.js';
import {
  SEND_NO_EMAIL_COPY,
  SEND_NO_PHONE_COPY,
  type CopyLinkOutcome,
  type CopyQuoteOutcome,
  type SendQuoteOutcome,
} from '../lib/actions.js';
import { Button } from '@ecmb/ui';
import { Banner } from './Banner.js';
import { CopyAffordances } from './CopyAffordances.js';

export type SendQuoteProps = {
  serverPhone: string | null;
  serverPhoneKnown: boolean;
  /** The address the SERVER would send to, same SEC-6 discipline as the phone.
   *  Both callers resolve it with `resolveRecipientEmail` — the send path's OWN
   *  helper (T549 M8): booking detail `customer.email` first, that booking's
   *  `inquiry_email` second, so this line can never name an address the server
   *  would not use, nor deny one it would. On the second branch the value is
   *  inquiry-supplied rather than customer-record data, which is what
   *  `serverEmailFromInquiry` states on screen.
   *  `serverPhoneKnown` covers BOTH: one booking-detail read lands them
   *  together, so it is really "the record has loaded" — and NOTHING here is
   *  treated as a recipient until it is true (see `targetFor`). */
  serverEmail: string | null;
  /** T549 M8 — TRUE when `serverEmail` is the address supplied on THIS
   *  booking's own inquiry (the public form) rather than the stored customer
   *  record. The recipient line says so, because M8's own reasoning names Helen
   *  as the only control on "is this really the customer's address?" and this is
   *  the screen she quotes from. Optional, defaulting to FALSE: a caller that
   *  says nothing gets no provenance claim, never a false "on file" one. */
  serverEmailFromInquiry?: boolean;
  /** What the customer asked for. NULL is the ordinary case (a call-in booking
   *  states nothing), never an error state. */
  contactPreference: string | null;
  /**
   * The latest QUOTE send off the ledger, or null for "never sent".
   *
   * T660 M4 (P0-7) adds `target` — WHERE that message actually went, straight
   * off `send_ledger.target_e164` (which has held phone-or-email since T549).
   * Without it the already-sent line was a hardcoded sentence naming a channel
   * and nothing else, so a booking whose customer record has since changed, or
   * whose address came from the inquiry rather than the record, gave Helen no
   * way to tell WHICH address the quote reached before she sends a second one.
   *
   * OPTIONAL, following `channelLabel?` / `channelConfigured?` below: every
   * fixed-prop <SendQuote> render in the suite stays valid, and an absent value
   * simply omits the recipient clause rather than printing `undefined` at Helen.
   */
  latestSend: { channel: string; deliveryStatus: string; target?: string | null } | null;
  sendStatusLabel: (status: string) => string;
  /** G3 registry label for a channel. OPTIONAL, and the default is the identity
   *  — which is precisely the registry accessor's own `?? channel` fallback, so
   *  an unwired caller renders the raw value rather than a hole. Both production
   *  call sites pass `channelLabel(registry, c)`. */
  channelLabel?: (channel: string) => string;
  /**
   * T524 (C5): can the SERVER actually send on this channel?
   *
   * ⚠️ T660 M4 (P0-6) widened what the answer decides. This used to say "Used
   * ONLY to pick the DEFAULT selection - nothing is ever hidden", and the first
   * half is no longer true: a `false` now ALSO makes the channel unselectable
   * (see `selectable` in the body) - the radio is announced `aria-disabled`, a
   * visible reason renders under it, and the pointer, Space/Enter and arrow
   * routes are all no-ops on it. The second half is unchanged and still
   * required: nothing is ever hidden, and the option keeps its tab stop.
   *
   * A function prop, not a registry, for the same reason `sendStatusLabel` and
   * `channelLabel` are: this component has no registry and both mount sites
   * inject their accessors. Optional, defaulting to "everything is configured",
   * mirroring `channelLabel?` directly above - a caller that says nothing gets
   * today's exact behaviour rather than a blank picker.
   */
  channelConfigured?: (channel: Channel) => boolean;
  /**
   * T660 M4 (plan step 4) — did the booking-detail read FAIL, as opposed to
   * being still in flight?
   *
   * `serverPhoneKnown` is false in BOTH states, so until now the block rendered
   * "Getting the customer's saved contact details..." for the whole job after a
   * failed read: no send, no copy affordance, and a sentence promising a load
   * that nothing was going to retry. That is the same class of lie P0-6 is about
   * (a state rendered as something it is not), which is why the plan folds it in
   * here rather than making it its own feature.
   *
   * DONE-ONLY in practice, and that is not an asymmetry to fix: BookingDetail
   * renders its own error screen (with a Try again button) instead of the
   * booking, so it can never mount this block on a failed read. It therefore
   * passes nothing and takes the default.
   *
   * Optional, defaulting to FALSE, following the props above: a caller that says
   * nothing claims no failure - the honest direction, since a false "we could
   * not load it" over a load that is simply slow would be a new lie.
   */
  contactReadFailed?: boolean;
  /**
   * ⭐ T1002 M7 — may this send OFFER to attach the deposit link?
   *
   * ⛔ FALSE ON A BOOKING WHOSE DEPOSIT IS ALREADY PAID, because there the
   * toggle does not merely add a useless link - it makes THE WHOLE SEND FAIL.
   * `handleSendQuote` throws DepositAlreadyPaidError on a `deposit_paid` row and
   * the route answers 409 `deposit_paid`, so switching it on turns a send Helen
   * needed into a refusal. A control whose only reachable effect is to break the
   * action beside it is not a choice; removing it is the fix.
   *
   * ⚠️ It also FORCES the flag off at the send call, not only out of the markup.
   * `includeDeposit` is component state that survives a prop flipping to false
   * mid-job (the deposit poll can land a payment while Helen is on this screen),
   * and a hidden switch that is still `true` would send the very request this
   * exists to prevent.
   *
   * Optional, defaulting to TRUE, following the six optional props above: every
   * fixed-prop <SendQuote> render in the suite, and Done.tsx, keep exactly
   * today's toggle without an edit.
   */
  offerDepositLink?: boolean;
  onSend: (includeDeposit: boolean, channel: Channel) => Promise<SendQuoteOutcome>;
  /**
   * T660 M2 — fetch the customer-facing quote message for the clipboard, or null
   * to withhold that button. The manual copy route now lives INSIDE this block
   * (P0-14: one component, two hosts) instead of being mounted separately beside
   * the phone number on each screen.
   *
   * A HANDLER, never a gate expression, and that is what preserves the
   * `quotePending` asymmetry each host needs (T524):
   *  - Done holds no store, so App.tsx wraps `copyQuoteMessage` in the
   *    re-price guard and hands the wrapped function down. Helen must not copy a
   *    message whose total differs from the one on the screen in front of her.
   *  - BookingDetail reads the persisted row and calls `copyQuoteMessage`
   *    directly - there is no live engine result on that screen to disagree with.
   * Unifying those into one guard here would be a second opinion about a question
   * each host already answers correctly.
   *
   * Optional, defaulting to null, following `channelLabel?` / `channelConfigured?`
   * directly above: a caller that says nothing gets no button rather than a
   * no-op one, and every fixed-prop <SendQuote> render in the suite stays valid.
   */
  onCopyMessage?: (() => Promise<CopyQuoteOutcome>) | null;
  /**
   * T660 M3 (P0-3) — fetch this booking's live deposit URL for the clipboard, or
   * null to withhold that button. Passed straight through to CopyAffordances,
   * which owns the clipboard write, the unmount guard and the caution.
   *
   * A HANDLER for the same reason `onCopyMessage` is one: both hosts already
   * know their own booking id and their own guards, and re-deriving either here
   * would be a second opinion. Unlike `onCopyMessage` it needs no re-price guard
   * on the Done side, and that is a finding rather than an omission - see
   * App.tsx's wiring, which states why.
   *
   * Optional, defaulting to null, following `onCopyMessage?` directly above:
   * every fixed-prop <SendQuote> render in the suite keeps exactly today's two
   * copy buttons.
   */
  onCopyPaymentLink?: (() => Promise<CopyLinkOutcome>) | null;
  /**
   * ⭐ Board 2814 M2 (DD-11 item 4) - ANOTHER DECISION HOLDS THE HOST SCREEN'S ONE
   * GOLD, so the send button paints quiet (DESIGN.md §5.2). Booking detail sets it
   * while the home's ask is open, whose Accept is that gold. Optional, defaulting
   * to false: Done and every fixed-prop render keep today's send button.
   */
  yieldGold?: boolean;
};

/** The failure as the action layer classified it. Keeping the KIND (rather
 *  than re-deriving it from the message text, as this component used to) means
 *  rewording any copy can never flip the banner style or the retry affordance. */
type Failure = { kind: 'not-configured' | 'failed'; message: string };

/**
 * The chip TONE for a delivery status (M7 review). The wording was already
 * honest, but every state — Queued, Delivered, Failed, Not delivered, Not sent —
 * rendered in the SAME navy chip, so a failed send looked like a routine queued
 * one to someone scanning mid-phone-call, which is the exact attribution M7
 * exists to fix. Tone only: it reuses the app's existing chip variants
 * (`chip--edited` error-600, `chip--paid` ok-600, `chip--estimate` navy) and
 * introduces no colour of its own.
 *
 * T608 M4 — the CLASSIFICATION moved out; only the TONE lives here. Deciding
 * "did the customer get this?" from (status, channel) is now
 * `sendOutcome` in shared/send-schema.ts, because M4's per-booking timeline is a
 * SECOND reader of the same ledger row: two screens deriving that answer
 * independently is how one of them ends up lying about it. The three FAILED /
 * DELIVERED / channel-dependent-'accepted' rules and their full reasoning moved
 * there verbatim; the rendered classes are unchanged (pinned by
 * test/client/send-ui.spec.tsx). 'unknown' — anything in flight
 * (composed/queued/sending) and anything unrecognised — stays NEUTRAL: a status
 * we cannot classify must never be coloured as good news.
 */
/**
 * The id of the line that says a channel's transport is off — now the FIELDSET's
 * description rather than a radio's (T1749 M8 round 2, impeccable P1-3).
 *
 * Deterministic and module-scoped, never generated: `App.tsx` switches on
 * `state.screen`, so Done and BookingDetail are alternatives and two SendQuotes
 * can never share a document. Derived from the channel because BOTH lines can be
 * on screen at once (both transports off is the launch state) and the group is
 * described by all of them.
 */
const channelOffId = (channel: Channel): string => `send-channel-off-${channel}`;

/** The id of the one sentence that says a send cannot be carried at all. */
const NOTHING_CAN_CARRY_ID = 'send-nothing-can-carry';

/**
 * ⛔ THE FIRST CUT OF `channelOffId` WAS DELETED AT T1749 M8 AND IS BACK, and the
 * round trip is the record. M8 first re-cut the off channel as a focusable line
 * with `aria-disabled`, which needed no id at all; round 2 measured that a
 * roleless tab stop is the defect and an `aria-disabled` generic announces
 * nothing, so the reason became the GROUP's description — which needs the id
 * again, pointing at a paragraph rather than at a radio's own hint.
 *
 * It minted `send-channel-off-<channel>` so an UNCONFIGURED channel's radio could
 * point `aria-describedby` at its reason (T660 M4, P0-6). M8 re-cut that option:
 * a channel whose transport is off no longer renders a control at all, so there
 * is nothing left to describe - the reason IS the rendered line, read from the
 * line itself rather than through an idref. An id whose only referrer has been
 * deleted is a dangling half of a relationship, not a spare part, so it went in
 * the same change. Nothing is hidden: see the off-channel line below, and the
 * ruling it stands on (`store/registry.ts`'s `isChannelConfigured`).
 */

/**
 * P0-16 — the ordinary case, said out loud.
 *
 * Measured on the real form: the website's contact-preference field is OPTIONAL
 * and defaults to "No preference", and every call-in and walk-in booking states
 * nothing at all. So on most bookings the picker was showing a pre-selected row
 * that LOOKED like the customer's choice and was in fact this component's
 * default. One sentence is the whole fix.
 */
const NO_PREFERENCE_COPY = 'They gave no contact preference, so this is the default.';

/**
 * Plan step 4 — the FAILED-DETAIL-READ state, which used to render as the
 * loading state forever.
 *
 * It names the remedy because there is none on this screen: nothing retries
 * `refreshBookingDetail`, and the block deliberately offers no recipient and no
 * copy row until the record has landed (unknown is not absent). Re-opening the
 * booking from Bookings re-runs the read and has its own Try again. No button
 * here: the operators abandoned a previous tool for having too many.
 */
const CONTACT_READ_FAILED_COPY =
  "The customer's saved contact details did not load, so nothing can be sent from here yet. "
  + 'Open this booking from Bookings to try again.';

function statusChipClass(status: string, channel: string): string {
  switch (sendOutcome(status, channel)) {
    case 'failed':
      return 'chip chip--edited';
    case 'delivered':
      return 'chip chip--paid';
    // 'sent' is NOT chip--paid: a handoff with no receipt must not be toned as
    // good news (board 1962). It shares the neutral tone with 'unknown' via the
    // default below, and is spelled out rather than left to fall through
    // because this switch is not exhaustiveness-checked - a silent default is
    // how the tone kept saying "delivered" while the word changed.
    case 'sent':
      return 'chip chip--estimate';
    default:
      return 'chip chip--estimate';
  }
}

/**
 * ⭐⭐ T1749 M8 (DS-20 a) — THE SENT STATE, IN ONE SENTENCE OFF THE LEDGER.
 *
 * It used to read `Sent by <channel> to <target>. Message status: <chip>` for
 * EVERY row, whatever the row said: a `failed` send and a `queued` one both
 * opened with the word "Sent", and the only thing that distinguished them was a
 * coloured chip at the end of the line. Helen scans this mid-phone-call, so the
 * state has to be the FIRST words, not the last paint.
 *
 * THREE SENTENCES, chosen by `sendOutcome` — the shared classifier the chip tone
 * and the booking timeline already use, never a second opinion about the same
 * ledger row:
 *  - `delivered` / `sent` → `Sent by <channel> to <target>.` The handoff really
 *    happened; the chip still carries WHICH of the two (board 1962: a handoff
 *    with no receipt is not toned as good news).
 *  - `failed` → `Sending failed: <channel> to <target>.`
 *  - `unknown` → `Not sent: <channel> to <target>.` Everything in flight
 *    (composed / queued / sending) and everything unrecognised. It claims no
 *    delivery and no failure, which is exactly what the ledger supports.
 *
 * ⛔ THE CHANNEL AND THE RECIPIENT RIDE EVERY ARM, because the attribution is
 * what M7 and M4 (P0-7) added and neither is allowed to fall out of the
 * not-sent arms: a booking quoted by email and re-quoted by text shows one
 * status, and without the channel there is no way to tell which message it
 * describes. The recipient comes off the LEDGER ROW, never off the customer
 * record — the record can have changed since, and an emailed quote may have gone
 * to an inquiry-supplied address that was never the record at all.
 * ⚠️ An absent `target` omits the clause rather than printing a hole (an older
 * server, or a hand-built fixture).
 */
export function sentSentence(
  latestSend: { channel: string; deliveryStatus: string; target?: string | null },
  channelLabel: (channel: string) => string,
): string {
  const where = `${channelLabel(latestSend.channel)}${latestSend.target ? ` to ${latestSend.target}` : ''}`;
  /* ⛔ THE POSITIVE ARM IS SPELLED OUT AND THE DEFAULT IS THE CONSERVATIVE ONE
     (T1749 M8, security round 2 L5). It used to fall THROUGH to `Sent by`, so a
     fifth outcome added to `SEND_OUTCOMES` would have claimed a send nobody had
     classified. The two outcomes that mean "it left" are named; everything else —
     in flight, unrecognised, a value from a newer server — reads `Not sent`,
     which is the direction this whole block is disciplined in (SEC-7). */
  switch (sendOutcome(latestSend.deliveryStatus, latestSend.channel)) {
    case 'sent':
    case 'delivered':
      return `Sent by ${where}.`;
    case 'failed':
      return `Sending failed: ${where}.`;
    default:
      return `Not sent: ${where}.`;
  }
}

/**
 * ⭐⭐ T1749 M8 (DS-20 b) — THE REFRESH-FAILED BLIND SPOT, SAID OUT LOUD.
 *
 * `sendQuoteToCustomer` sends, and THEN calls `refreshBookingDetail` to render
 * the true ledger row (SEC-7: never an optimistic 'sent'). When that second read
 * fails, its catch writes `serverDetailFailed: true` and `latestSend` stays
 * null — so the block rendered the PRE-SEND state: no status line, and a gold
 * `Send quote to customer` reading as though nothing had gone out. Helen sends
 * again, and on a with-deposit send that mints a second payment link.
 *
 * The send's own outcome is the missing half, and it is local to this component
 * (`sentOk`), because it is the only thing that saw the 2xx. Combined with the
 * host's `contactReadFailed` — Done already threads `job.serverDetailFailed` for
 * the recipient copy — it is exactly "the send worked and the read after it did
 * not".
 */
const SENT_UNREFRESHED_COPY = 'Sent, details could not be refreshed.';

/** The three props that decide WHO a channel would reach. Named as a type so
 *  `targetOf` and its exported predicate below take the same record the
 *  component holds, rather than a positional restatement of it. */
type SendRecipient = Pick<SendQuoteProps, 'serverPhone' | 'serverPhoneKnown' | 'serverEmail'>;

/**
 * Who this channel would actually reach, per the stored record — and NOTHING
 * counts as a recipient until that record has LANDED.
 *
 * The `serverPhoneKnown` gate is load-bearing, not defensive. `serverPhone`
 * has exactly one writer (refreshBookingDetail), so gating on its value was
 * incidentally an is-loaded check; `serverEmail` does NOT — the Done surface
 * feeds it from `job.customerEmail`, which `sourceFromInquiry` ALSO prefills
 * from the PUBLIC inquiry form. Without this gate the pre-load render names a
 * customer-supplied address as "the customer" and arms the send button on it,
 * while suppressing the honest loading copy (the `serverPhoneKnown` branch in
 * the body is only ever reached when there is no target). And the window is not
 * even guaranteed transient: refreshBookingDetail's catch leaves the flag
 * false, so a failed detail read would hold it open for that whole job.
 *
 * At MODULE scope (T660 M6 R1) rather than in the component body, so the host
 * screen can ask the same question about the same props without a second copy
 * of the rule — the drift `copyPhone`'s note further down names as the defect.
 */
function targetOf(c: Channel, r: SendRecipient): string | null {
  return r.serverPhoneKnown ? (c === CHANNEL_EMAIL ? r.serverEmail : r.serverPhone) || null : null;
}

/**
 * T660 M6 (R1) — does this block put a PRIMARY control on screen for these props?
 *
 * P0-22 moved the gold onto the send and made Done's 'New Quote' unconditionally
 * secondary, which stranded three Done states with NO primary at all: a parked
 * save and a price check never mount this block, and its already-sent arm is
 * `btn--quiet` by M4's own design. `Done.tsx` asks THIS rather than restating the
 * rule, because a restatement is how the two drift: the send affordance also
 * needs a REACHABLE channel (the fourth stranded state — a saved, quoted booking
 * with no phone and no email renders this block with no send button in it at all).
 *
 * Both conjuncts are the body's own expressions, not near-twins of them:
 * `latestSend` is exactly what picks `btn--quiet` over `btn--primary` on the send
 * button, and the channel test is `offered`'s (`IMPLEMENTED_CHANNELS` filtered by
 * `targetOf`) — `target` is `targetOf(channel)` and `channel` falls back through
 * `offered`, so a non-empty `offered` is what makes the send affordance render.
 *
 * simplified: it reads PROPS only, so it cannot see `chosen` — if Helen moves the
 * picker onto a configured channel with no recipient on file the send button
 * disappears mid-interaction and Done keeps its secondary exit. Lifting `chosen`
 * to the host is the fix; do it if that state is ever reported.
 */
export function sendQuoteHasPrimary(
  p: SendRecipient & Pick<SendQuoteProps, 'latestSend'> & {
    /**
     * ⭐⭐ T1749 M8 (security round 2, Med2) — DID A SEND ON THIS MOUNT SUCCEED?
     *
     * `latestSend` is the ledger's answer and it arrives through a SECOND read
     * (`refreshBookingDetail`) that can fail — and on a RE-send it can be a stale
     * row from send #1, which is a truthy value describing the wrong send. The
     * host's own witness of the 2xx outranks it: once a send has succeeded, the
     * send is no longer the thing to do next whatever the ledger says, so the
     * screen's gold moves on exactly as the ledger row would have moved it.
     * Optional, defaulting to "no send seen", so every existing caller keeps
     * today's answer.
     */
    sentOk?: boolean;
  },
): boolean {
  if (p.sentOk === true) return false;
  return p.latestSend === null && IMPLEMENTED_CHANNELS.some((c) => targetOf(c, p) !== null);
}

export function SendQuote({
  serverPhone,
  serverPhoneKnown,
  serverEmail,
  serverEmailFromInquiry = false,
  contactPreference,
  latestSend,
  sendStatusLabel,
  channelLabel = (c) => c,
  // T524 (C5): defaults to "everything is configured", so a caller that wires
  // nothing gets today's exact behaviour rather than a blank picker.
  channelConfigured = () => true,
  contactReadFailed = false,
  offerDepositLink = true,
  onSend,
  onCopyMessage = null,
  onCopyPaymentLink = null,
  yieldGold = false,
}: SendQuoteProps) {
  const [includeDeposit, setIncludeDeposit] = useState(false);
  const [busy, setBusy] = useState(false);
  /** DS-20 b — did a send on THIS mount return ok? The only witness to the 2xx
   *  when the read after it fails; see SENT_UNREFRESHED_COPY. Component state,
   *  like `chosen`, and blown away by the hosts' `key={bookingId}` remount, so
   *  it can never describe a different booking. */
  const [sentOk, setSentOk] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  // null = "Helen has not chosen", NOT "sms". Storing the DEFAULT here instead
  // would freeze it at mount, and the booking detail (which is what says whether
  // an email exists at all) lands asynchronously after that — so the default is
  // derived on every render and only an explicit choice is remembered.
  const [chosen, setChosen] = useState<Channel | null>(null);

  /** The module-scope rule above, bound to this render's props — so the four
   *  call sites below and the host screen's `sendQuoteHasPrimary` can never
   *  disagree about who a channel reaches. */
  const targetFor = (c: Channel): string | null =>
    targetOf(c, { serverPhone, serverPhoneKnown, serverEmail });

  /**
   * ⭐⭐ HAS THIS QUOTE GONE OUT? The ledger's answer OR this mount's own witness
   * (T1749 M8 round 2, security Med2). `latestSend` is a truthy row that may
   * describe an EARLIER send (a re-send whose refresh failed keeps send #1's row),
   * and `sentOk` is the 2xx this component watched, so the two together are the
   * honest answer to "is the send still the thing to do next".
   */
  const sent = latestSend !== null || sentOk;

  // The channels offered come from IMPLEMENTED_CHANNELS — the same list the send
  // path validates against — NOT from the registry's `channels` block, which
  // also carries 'whatsapp' (a real ledger channel for OPERATOR alerts, with no
  // customer-send driver: requesting it is a 400). The registry supplies the
  // LABELS only. Deliberately never a membership test against the registry:
  // every hand-built fixture in the test suite carries empty channel values, and
  // a validity gate would read those as "no channel exists".
  const offered = IMPLEMENTED_CHANNELS.filter((c) => targetFor(c) !== null);
  /**
   * The number the MANUAL copy route puts on the clipboard: `targetFor(sms)`,
   * the very expression the SMS channel is offered on.
   *
   * Deriving it here rather than taking a prop is what makes the two hosts
   * agree. Before T660 M2 each screen mounted CopyAffordances itself and each
   * wrote the gate out by hand - Done as `serverPhoneKnown ? serverPhone : null`
   * and BookingDetail as a bare `customer.phone_e164` beside a hardcoded
   * `serverPhoneKnown={true}`. Both reduce to this, so nothing changes; but two
   * hand-written copies of one rule is how the two surfaces drift apart, which
   * is the whole defect P0-14 names.
   *
   * E.164, not the local form Done renders beside it: WhatsApp needs the country
   * code (see CopyAffordances' `phone` prop).
   */
  const copyPhone = targetFor(CHANNEL_SMS);
  const preferred: Channel = contactPreference === CHANNEL_EMAIL ? CHANNEL_EMAIL : CHANNEL_SMS;
  /**
   * The DEFAULT selection. Both radios still render, `offered` is unchanged, and
   * nothing is hidden.
   *
   * ⚠️ T660 M4 (P0-6) REVERSED the clause this used to close on: "Helen must
   * still be able to pick SMS (she may know it is about to be switched on)".
   * She cannot, since M4 - `selectable` below gates SELECTION on configuration
   * too, so this derivation is no longer the only thing config decides. What
   * survives is the other half, unchanged: the option is still OFFERED, visible,
   * focusable and says why it is off.
   *
   * The new condition CONJOINS with `targetFor`; it must never replace it.
   * Substituting would default to a channel with no recipient on file - a
   * "preferred" email for a customer whose email we do not have - which
   * send-ui.spec.tsx:351 pins directly ("a stated preference with no address on
   * file falls back to a reachable channel").
   *
   *  1. the stated preference, if it is BOTH reachable and configured;
   *  2. otherwise the first reachable channel that is also configured;
   *  3. otherwise exactly today's answer, which covers both "nothing is
   *     configured" (never blank the picker over a config hint) and "`offered`
   *     is empty" (the no-contact-details case, where the no-recipient copy must
   *     still speak about the channel the customer asked for).
   */
  const channel: Channel = chosen
    ?? (targetFor(preferred) && channelConfigured(preferred)
      ? preferred
      : offered.find(channelConfigured) ?? offered[0] ?? preferred);
  const target = targetFor(channel);
  /**
   * T524 (C5), from security-review: the default flip must not SILENTLY outrank
   * a customer's stated wish.
   *
   * `contact_preference` is a real choice the customer made on the public form,
   * and before C5 an unconfigured preferred channel produced a 503 - an explicit
   * moment for Helen to decide to override it. C5 removes that moment, so
   * without this the screen would quietly text a customer who asked for email
   * and say nothing. The server refuses the analogous case at the API layer and
   * its own comment calls it "the dishonest branch".
   *
   * Deliberately narrow, and every conjunct earns its place:
   *  - `chosen === null`: if Helen picked the channel herself there is nothing
   *    to explain.
   *  - the preference must be one the picker can actually honour. 'call' maps to
   *    `preferred = sms`, but a customer who asked to be RUNG did not ask to be
   *    texted, so claiming "they asked for text" would be a new false statement.
   *  - `targetFor(preferred) !== null`: an UNREACHABLE preference (no email on
   *    file) already falls back, and has done since M7. That is pre-existing
   *    behaviour with its own honest recipient line; annotating it here would be
   *    new copy for old behaviour.
   * So this fires only when the preference was reachable and CONFIGURATION is
   * what overrode it - which is exactly what C5 introduced.
   */
  const statedPreference: Channel | null =
    contactPreference === CHANNEL_EMAIL ? CHANNEL_EMAIL
      : contactPreference === CHANNEL_SMS ? CHANNEL_SMS : null;
  const preferenceOverriddenByConfig =
    chosen === null
    && statedPreference !== null
    && channel !== statedPreference
    && targetFor(statedPreference) !== null
    && !channelConfigured(statedPreference);

  /**
   * T660 M4 (P0-6) — can Helen SELECT this channel? Configuration, and only
   * configuration.
   *
   * ⚠️ THIS REVERSES HALF OF T524 (C5), deliberately, and the reversal is the
   * milestone. C5 rendered an unconfigured channel as an ordinary, fully
   * pickable option and left the refusal to the server ("Helen may know it is
   * about to be switched on"). With SMS vendor-blocked and email transport
   * unbuilt, "switched off" is not an edge case Helen occasionally meets - it is
   * the state she lives in at launch, so an option that looks live, accepts a
   * tap, and then produces a 503 is the screen lying to her about what it can
   * do. What C5 got right is kept intact and is the other half of P0-6: the
   * channel is still OFFERED, still visible, still focusable, and still says why
   * it is off. Nothing is hidden.
   *
   * ⚠️ T1749 M8 SUPERSEDES THE MECHANISM, NOT THE RULING. This paragraph used to
   * explain why the option was announced `aria-disabled` rather than given the
   * native `disabled` attribute (which would drop it out of the tab order, so a
   * keyboard user could never DISCOVER the transport is off) and why each of the
   * three activation routes - pointer, Space/Enter, arrows - then had to be
   * suppressed by hand. M8 stops rendering a control for an off channel at all
   * (see `radios` below), so there is no activation route left to suppress and
   * the `toggle-row--off` class is retired from the sheet with it. Discoverable
   * and announced is unchanged: the line carries `tabindex=0` and
   * `aria-disabled="true"`.
   *
   * UNIFORM, with no "unless nothing at all is configured" escape hatch. Such a
   * hatch would produce a row marked off that is nevertheless pickable, which is
   * precisely the contradiction this exists to remove. The DEFAULT derivation
   * above is untouched, so the all-off booking still lands on a sensible channel
   * (branch 3 - never blank the picker over a config hint) and the send
   * affordance still renders on it; what Helen loses is only the ability to
   * MOVE the selection onto a transport that cannot carry it.
   */
  const selectable = (c: Channel): boolean => channelConfigured(c);
  /**
   * ⭐⭐ T1749 M8 (DS-20 c) — THE OPTIONS THAT ARE ACTUALLY OPTIONS.
   *
   * M4 rendered an unconfigured channel as a radio that announced
   * `aria-disabled` and then had to suppress the pointer, Space/Enter and the
   * arrow keys BY HAND, one route at a time, because `aria-disabled` suppresses
   * nothing on its own. Every one of those suppressions was a chance to miss a
   * route. The ruling instead is that a channel that cannot carry the message is
   * not a pickable thing at all: it renders as a quiet LINE (below), so there is
   * no radio to select, no box to tap and no glyph that looks pickable — and the
   * three by-hand suppressions are gone with the control they guarded.
   *
   * ⛔ NOTHING IS HIDDEN, which is the half of P0-6 and of C5 that stands
   * unchanged (`store/registry.ts`'s `isChannelConfigured` ruling): the channel is
   * still on screen, still says why it is off, and is still reachable by keyboard
   * — the line carries `tabindex=0`, so a keyboard user still meets it.
   * ⚠️ `offered` IS UNTOUCHED and still decides whether the group renders at all;
   * this only splits it into the radios and the lines, in `offered` order.
   */
  const radios = offered.filter(selectable);
  /** The offered channels whose transport is OFF — one state line each, and the
   *  group's own description (T1749 M8 round 2). */
  const offLines = offered.filter((c) => !selectable(c));
  /**
   * ⭐⭐ NOTHING CAN CARRY THIS QUOTE, and it is the state the first cut rendered
   * as a live gold button that did nothing (impeccable round 2, P1-2: a
   * `btn--primary` at full gold-500 strength, `disabled:false`,
   * `aria-disabled:null`, beside its own copy saying "nothing will be sent").
   * §7.3 is explicit — friendly copy, never a dead button — so the primary takes
   * §5.1's disabled treatment instead and says why, once, underneath.
   * ⛔ IT IS NOT "the picker is empty": `offered` can be non-empty (a reachable
   * channel exists) while every one of them is switched off, which IS the launch
   * state (SMS vendor-blocked, email transport unbuilt).
   */
  const nothingCanCarry = offered.length > 0 && radios.length === 0;

  /**
   * P0-15 / P0-16 / P0-17 — ONE sentence saying WHY this row is pre-selected.
   *
   * The requirement the three share is that the call route and the web route
   * must differ only in (a) which row is pre-selected and (b) one sentence
   * stating why. Before this they differed in NEITHER visibly: a call-in
   * booking states no preference, `preferred` maps everything that is not an
   * exact 'email' to SMS, and the resulting default rendered as a checked radio
   * indistinguishable from a customer's actual choice. Alex saw exactly that -
   * Text message pre-selected on a web inquiry that had an email on file.
   *
   * ⚠️ ONE sentence, and never a FOURTH near-duplicate. Two clauses already
   * live in the recipient paragraph (the M8 address provenance and the C5
   * config override), and the arms below are mutually exclusive with both:
   *  - `chosen !== null`: Helen picked it herself, so there is nothing to
   *    explain. The same first conjunct `preferenceOverriddenByConfig` uses.
   *  - `preferenceOverriddenByConfig`: that sentence already states the reason,
   *    and it is the more specific one. Saying both would print two
   *    explanations of one selection.
   * At most one of these three ever renders, which is what keeps the paragraph
   * readable and keeps every unqualified `getByText` on it matching one node.
   *
   * `contactPreferenceLabel` rather than `channelLabel`, and the difference is
   * the P0-16 defect itself: 'call' is a real stated preference that this picker
   * has no channel for, and the channel registry would render it as the SMS row
   * it silently became. The shared helper says "Phone call" (and keeps the
   * retired 'whatsapp' readable), so arm 3 can state what they actually asked
   * for instead of a channel they did not.
   *
   * The wording deliberately avoids the exact phrase the C5 sentence opens with
   * ("They asked for ..."), so the two remain distinguishable to a reader and to
   * the pins that assert the C5 clause is absent in states where it would be a
   * false claim.
   */
  const statedPreferenceLabel = contactPreferenceLabel(contactPreference);
  /**
   * ⛔ AND NOT WHEN NOTHING CAN CARRY THE QUOTE (T1749 M8 round 3). The sentence
   * explains why a row is PRE-SELECTED; with every transport off there is no
   * selection to explain, and the block already says so twice — once per channel,
   * and once under the refusing primary. It was a third explanation of a choice
   * the screen cannot offer, and it cost the state a line of the fold.
   * ⚠️ The same first conjunct the C5 clause uses (`chosen !== null`), extended
   * by the one state the picker has no answer in.
   */
  const preferenceNote: string | null =
    chosen !== null || preferenceOverriddenByConfig || nothingCanCarry
      ? null
      // Nothing stated: the ordinary case on BOTH routes (a call-in says
      // nothing; the web form's field is optional and defaults to "No
      // preference"), so it is said in words rather than left to look like a
      // choice. P0-16.
      : statedPreferenceLabel === null
        ? NO_PREFERENCE_COPY
        // Stated, and honoured - the web route with a preference this picker can
        // reach and send on. P0-15.
        : statedPreference !== null && channel === statedPreference
          ? `Pre-selected because the customer asked for ${statedPreferenceLabel}.`
          // Stated, and NOT what is about to be used: either the picker has no
          // such channel ('call', or a retired value) or there is no address on
          // file for it. One clause covers both, because the honest common fact
          // is that we are not using what they asked for; naming a cause would
          // be right in one case and false in the other.
          : `The customer asked for ${statedPreferenceLabel}, which is not usable here, so this is the default.`;

  /** The one place a POINTER can change the channel. ⚠️ T1749 M8: it carries no
   *  `selectable` guard any more, and that is the point of the re-cut above —
   *  only a SELECTABLE channel renders a radio, so an off channel has no pointer
   *  route to suppress. The gate moved from three hand-written suppressions to
   *  one filter. */
  const pickChannel = (c: Channel): void => {
    setChosen(c);
  };

  /**
   * WHICH option holds the group's single Tab stop - an INDEX, not an identity
   * test against `channel`, and that is the whole point.
   *
   * `chosen` is remembered across re-renders while `offered` is re-derived on
   * every one, so a refresh that SHRINKS `offered` (Helen picks Email, then the
   * resolved email comes back null) leaves the remembered channel outside it.
   * A bare `c === channel` would then put -1 on EVERY radio and the group would
   * drop out of the Tab sequence entirely - unreachable by keyboard, with no key
   * that brings it back. The search returns -1 in exactly that state and
   * `Math.max` turns it into the APG answer: when no option is checked, the
   * FIRST option is the group's Tab stop. (`findIndex` rather than `indexOf`:
   * `offered` is narrowed to the two IMPLEMENTED channels while `channel` is the
   * full `Channel` union, which `indexOf` rejects and `===` accepts.)
   *
   * Latent, not live: no refresh path on either host clears an email today and
   * `serverPhoneKnown` never reverts, so this is a floor rather than a fix.
   * Deliberately a render-time derivation and NOT an effect that clears `chosen`
   * when it leaves `offered` - there is nothing here that can loop, and it does
   * not silently move Helen's choice onto a channel she did not pick (the
   * recipient copy above then honestly says there is no address for the one she
   * did).
   */
  const tabStop = Math.max(radios.findIndex((c) => c === channel), 0);

  /**
   * The ARIA APG RADIO GROUP keyboard pattern, which `role="radiogroup"` below is
   * a promise to implement and (until now) did not.
   *
   * Two things were missing and they are one defect: the options are native
   * <button>s, so every one of them was independently Tab-focusable while the
   * arrow keys did nothing. A keyboard user landed in an N-stop group whose
   * documented navigation keys were dead. Space/Enter kept working throughout —
   * that is the native button behaviour, and it is why these stay <button>s
   * rather than becoming <input type="radio">: the toggle-row styling and the
   * existing `role="radio"` pins both hang off this element.
   *
   * Selection FOLLOWS focus, per APG: each arrow both moves focus and checks the
   * newly-focused option. `preventDefault` stops the arrows scrolling the page
   * instead. Home/End are the APG-optional pair and cost two lines given the
   * index maths.
   *
   * ⚠️ T660 M4's EXCEPTION IS GONE AT T1749 M8, and selection follows focus with
   * no condition again. M4 had the arrows move focus onto an unconfigured option
   * without selecting it - the "focused but unchecked" state - because such an
   * option was still a radio in this group. It is not one any more (see
   * `radios`), so this walk only ever lands on options that can be selected.
   *
   * Focus is moved by walking the DOM rather than by a ref array or an effect.
   * The buttons are keyed by channel, so Preact re-uses the same nodes across the
   * re-render this setter triggers, and the node under the cursor stays focused —
   * which also means this cannot fight the `key={bookingId}` remount discipline
   * the hosts apply to the whole component: a remount blows the group away
   * wholesale, there is no stale ref to point at a dead node.
   *
   * ⚠️ It queries `[role="radio"]` rather than indexing `parentElement.children`,
   * and that is load-bearing since M4 and MORE so since M8: an off channel's
   * quiet line is a SIBLING of the radios inside `.channel-choice`, so a
   * positional index into `children` would land on that line and focus nothing.
   * Querying by role makes the walk independent of anything else that shares the
   * container, and keeps the DOM order in step with the `radios` list above.
   */
  const onRadioKeyDown = (e: KeyboardEvent, index: number) => {
    const last = radios.length - 1;
    let next: number;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    e.preventDefault();
    // In range by construction: every arm above lands in [0, last], and this
    // handler only exists on a radio, which only renders when `radios` is
    // non-empty. `noUncheckedIndexedAccess` cannot see that.
    const landing = radios[next]!;
    // Selection follows focus, unconditionally (APG). ⚠️ T1749 M8 dropped the
    // `if (selectable(landing))` guard M4 needed: `radios` is the SELECTABLE
    // subset, so every landing is selectable and the guard could only ever be
    // true. The "focused but unchecked" state it created is gone with it.
    setChosen(landing);
    // `radioNodes`, not `radios`: the derivation above is the CHANNEL list and
    // this is the DOM list of the same things, in the same order.
    const radioNodes = (e.currentTarget as HTMLElement | null)
      ?.parentElement?.querySelectorAll('[role="radio"]');
    const sibling = radioNodes?.[next];
    if (sibling instanceof HTMLElement) sibling.focus();
  };

  const send = async () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    // ⛔ THE PROP WINS OVER THE STATE. See `offerDepositLink`: the switch can be
    // left `true` from before a deposit was paid, and sending that flag on a
    // paid booking 409s the WHOLE send.
    const res = await onSend(offerDepositLink && includeDeposit, channel);
    setBusy(false);
    if (!res.ok) setFailure({ kind: res.kind, message: res.message });
    else setSentOk(true);
  };

  return (
    <div class="send-quote">
      <hr class="section-rule" />
      {/* T660 B7: an <h2>, not a <p>. This is the section heading for the whole
          delivery block and it already looked like one; only the semantics were
          missing, so a screen-reader user could not jump to it and the two
          groups below hung off nothing. The CLASS and the TEXT are unchanged -
          `Send quote to customer` has 21 references across src/, test/ and e2e/,
          and the send BUTTON keeps the same words (they are disambiguated by
          role, as they already were when this was a paragraph). */}
      <h2 class="field-heading">Send quote to customer</h2>
      {/* EXACTLY ONE recipient line, whose target varies with the channel. A
          second line would read as two recipients (and breaks the e2e net's
          unqualified getByText on this sentence) — which is why the M8
          provenance rides INSIDE this paragraph as a trailing clause rather
          than as a paragraph of its own. It names no second address. */}
      {target ? (
        <p class="recipient-copy">
          Goes to the customer at {target}. Not the team.
          {channel === CHANNEL_EMAIL && serverEmailFromInquiry && (
            <span class="recipient-source"> This address came from their inquiry, not their customer record.</span>
          )}
          {/* Inside THIS paragraph, as a trailing clause, following the M8
              provenance span directly above - a paragraph of its own would read
              as a second recipient and would break the e2e net's unqualified
              getByText on this sentence. */}
          {preferenceOverriddenByConfig && (
            <span class="recipient-source">
              {' '}They asked for {channelLabel(statedPreference!)}, which is switched off here.
            </span>
          )}
          {/* P0-15/16/17, and inside THIS paragraph for the same reason the two
              clauses above are: a sibling <p> would read as a second recipient
              and would break the e2e net's unqualified getByText on this
              sentence. Mutually exclusive with the override clause directly
              above (see the derivation), so the paragraph never carries two
              explanations of one selection. */}
          {preferenceNote !== null && (
            <span class="recipient-source"> {preferenceNote}</span>
          )}
        </p>
      ) : serverPhoneKnown ? (
        // KNOWN absent — honest no-recipient copy for THIS channel (shared constants).
        <p class="recipient-copy">{channel === CHANNEL_EMAIL ? SEND_NO_EMAIL_COPY : SEND_NO_PHONE_COPY}</p>
      ) : contactReadFailed ? (
        // T660 M4 (plan step 4) — the read FAILED. Unknown is still not absent,
        // so this claims nothing about whether the customer has contact details;
        // what it stops claiming is that a load is still on its way. Nothing
        // retries this path, so the sentence names the route that does.
        <p class="recipient-copy">{CONTACT_READ_FAILED_COPY}</p>
      ) : (
        // Still in flight — unknown is NOT absent. Channel-neutral since M7:
        // this one read lands the phone AND the email.
        <p class="recipient-copy">Getting the customer's saved contact details…</p>
      )}

      {/* GROUP 1 of 2 (P0-1) — the implemented CUSTOMER channels, and nothing
          else. The boundary in the accessibility tree ends where the radio
          options end: a radiogroup is a promise that everything inside it is one
          mutually-exclusive choice, so the deposit switch, the send button and
          the copy buttons all stay OUTSIDE it or they read to a screen reader as
          further channels to pick from. Pinned by copy-affordances.spec.tsx
          (`closest('[role="radiogroup"]')` is null for both copy buttons).

          A <fieldset>/<legend>, so the group's name is VISIBLE as well as
          announced - one string doing both jobs, with no id to collide when both
          hosts render in one document. `role="radiogroup"` overrides the
          fieldset's implicit `group`; the legend still names it (HTML-AAM names
          a fieldset from its legend, and the parity spec checks that rather than
          assuming it). The radios keep their own flex container, so this adds a
          reset rule and changes no layout.

          T660 M2 (P0-8) deleted the `offered.length > 1` collapse that used to
          wrap this. "One reachable channel is not a decision" was wrong in the
          only direction that matters: on a call-in booking - the ordinary ECMB
          job, phone and nothing else - the picker vanished entirely, so the
          screen never said WHICH channel the send was about to use, and the
          status line afterwards named a channel Helen had never been shown.
          `offered.length > 0` remains: that is "no reachable channel at all"
          (the not-loaded and no-contact-details states, which the recipient copy
          above already speaks to), never "only one". */}
      {offered.length > 0 && (
        /* ⭐⭐ T1749 M8 round 2 (impeccable P1-2) — THE GROUP STOPS CLAIMING A ROLE
           IT CANNOT HONOUR. `role="radiogroup"` is a promise that everything
           inside it is one mutually-exclusive CHOICE; with every transport off
           there are no radios at all, so a screen-reader user landed on a group
           named "Send it" with nothing to pick (measured live on three states:
           Done phone-only, Done phone+email, and the booking detail). The
           attribute is now conditional on there being at least one radio, and the
           fieldset's implicit `group` role — which is honest about a box of
           related content — is what is left.
           ⛔ IT IS STILL A FIELDSET WITH A LEGEND, so the group's name is visible
           AND announced either way, and nothing is hidden (Q135).
           ⚠️ `aria-describedby` NAMES EVERY OFF LINE, so the reason is read when a
           reader enters the group rather than sitting in the tab order as a
           roleless stop (P1-3). The ids are emitted on exactly the condition the
           lines are, so no idref can dangle. */
        <fieldset
          class="delivery-group"
          role={radios.length > 0 ? 'radiogroup' : undefined}
          aria-describedby={offLines.length > 0 ? offLines.map(channelOffId).join(' ') : undefined}
        >
          <legend class="delivery-group-label">Send it</legend>
          <div class="channel-choice">
            {offered.map((c) => {
              // T660 M4 (P0-6): admissible, reachable, and its transport is off.
              if (!selectable(c)) {
                /* ⭐⭐ T1749 M8 round 2 (impeccable P1-2/P1-3, ux and webapp graded
                   the same line) — IT IS A STATE LINE, NOT A WIDGET.
                   M8's first cut made it a `<span class="field-hint" tabIndex={0}
                   aria-disabled="true">`, which was wrong three ways, all measured
                   live: `aria-disabled` is not exposed on a generic element, so
                   the claim did not hold; a roleless paragraph in the tab order is
                   a WCAG defect rather than discoverability (main's only two
                   `[tabindex="0"]` nodes were these); and `.field-hint` is the HINT
                   treatment, which Q139 moved off gold and reserves for hints —
                   the comment that cited "the gold the copy rows' refusal lines
                   use" was describing a sheet that has since changed.
                   ⇒ a plain `<p class="send-status">`, the treatment this block
                   already uses for the state it reports, and the FIELDSET points
                   `aria-describedby` at it — so the group announces the reason
                   when a reader enters it, which is what "keyboard-discoverable"
                   (Q135) actually requires. Nothing is hidden and nothing is
                   focusable that cannot be operated.
                   ⛔ ONE SHORT SENTENCE PER CHANNEL. The two-arm version carried a
                   second clause for the off-AND-selected case ("It stays selected
                   because nothing else can carry this quote"); that fact is now
                   stated ONCE under the block, beside the refusing primary, rather
                   than repeated per channel — which is also what lets a customer
                   with two off channels keep the primary inside the ~700 fold
                   (impeccable P2-5: each line cost ~51px).
                   ⚠️ The 'is not switched on here' opening is unchanged, which is
                   what the both-off pin in send-ui.spec.tsx counts. */
                return (
                  <p key={c} id={channelOffId(c)} class="send-status">
                    {`${channelLabel(c)} is not switched on here.`}
                  </p>
                );
              }
              /* The index into the RADIOS, never into `offered` — the roving
                 tabindex and the arrow walk both index the rendered radios, and
                 an off line is not one of them. */
              const i = radios.indexOf(c);
              return (
                  <button
                    key={c}
                    type="button"
                    class={`toggle-row toggle-row--tappable${c === channel ? ' toggle-row--on' : ''}`}
                    role="radio"
                    aria-checked={c === channel}
                    /* ONE Tab stop for the whole group (APG roving tabindex): a
                       native <button> defaults to 0, so before this every channel
                       was its own stop. Keyed off `tabStop` (an index) rather than
                       `c === channel`, so exactly one radio holds 0 even in the one
                       state where the checked channel is not a member of `radios` —
                       see the derivation above. */
                    tabIndex={i === tabStop ? 0 : -1}
                    onKeyDown={(e) => onRadioKeyDown(e, i)}
                    onClick={() => pickChannel(c)}
                  >
                    <span class="toggle-label">{channelLabel(c)}</span>
                  </button>
              );
            })}
          </div>
        </fieldset>
      )}

      {target && (
        <>
          {/* T1002 M7 — WITHDRAWN on a paid deposit (see `offerDepositLink`).
              Nothing takes its place: there is no state to explain here, because
              the paid state is already announced by the "Deposit paid" banner at
              the top of the booking record, and a second line about a control
              that is not there would be noise on a screen whose whole rule is
              "too many buttons is how the last tool failed". */}
          {/* ⭐⭐ T1749 M8 round 3 (impeccable C8) — IT REFUSES WITH THE SEND, because
              a switch that arms a message nothing can carry is the same dead
              control §7.3 forbids on the button beside it. It takes the SAME
              treatment as the primary: `aria-disabled` (never the attribute — it
              stays reachable so it can explain itself, §5.1), the description
              pointing at the one reason line above, and every activation route
              refused by hand (the class comes off, the handler is withheld, and
              the keyboard default is cancelled — `aria-disabled` suppresses
              nothing on its own, which is the fact this file records twice).
              ⚠️ `includeDeposit` is NOT forced false here: the prop-over-state
              rule in `send()` already does that for the paid case, and this state
              cannot send at all. */}
          {offerDepositLink && (
          <button
            type="button"
            class={`toggle-row${nothingCanCarry ? '' : ' toggle-row--tappable'}${includeDeposit ? ' toggle-row--on' : ''}`}
            role="switch"
            aria-checked={includeDeposit}
            aria-disabled={nothingCanCarry ? 'true' : undefined}
            aria-describedby={nothingCanCarry ? NOTHING_CAN_CARRY_ID : undefined}
            onKeyDown={nothingCanCarry ? (e: KeyboardEvent) => { if (e.key !== 'Tab') e.preventDefault(); } : undefined}
            onClick={nothingCanCarry ? undefined : () => setIncludeDeposit(!includeDeposit)}
          >
            <span class="toggle-label">Include the deposit link</span>
            <span class="toggle-knob" aria-hidden="true" />
          </button>
          )}
          {/* ⭐⭐ T1749 M8 round 3 (impeccable C1) — THE REASON IS THE CAPTION OF THE
              BUTTON IT DESCRIBES, so it sits ABOVE it. Below the action row it
              started at y 702.3 on a two-off-channel Done: the cause in view and
              the consequence past the ~700 plane, which is the half of the state
              an operator needs. The `aria-describedby` link is unchanged — order
              in the DOM is not what makes a description reach a reader. */}
          {nothingCanCarry && (
            <p id={NOTHING_CAN_CARRY_ID} class="send-status">
              Nothing can carry this quote until a channel is switched on.
            </p>
          )}
          <div class="action-row">
            {/* T660 M4 (P0-7) — a SECOND send must be deliberate, not the
                obvious next tap.
                  - the NAME changes, which is the part that actually decides
                    it: 'Send quote to customer' reads as the thing you came
                    here to do, and it kept reading that way on a booking that
                    had already been quoted, beside a status line saying so;
                  - the VARIANT drops from `primary` to `quiet`, so it stops
                    being the visually loudest control on the block.
                Deliberately not a confirm step: the operators bounced off a
                previous tool for having too many buttons, and the honest status
                line directly below already says what went where.

                'Send again' is collision-free against every locked name on this
                screen in BOTH harnesses - Playwright matches accessible names by
                case-insensitive SUBSTRING while testing-library matches a string
                name exactly, so the check has to run in both directions. It is
                neither a substring nor a superstring of 'Send quote to customer',
                'Send it' (the group's legend), 'Try again' (the failure banner's
                retry) or any copy-row label.

                ⭐⭐ T1749 M8 round 2 — TWO CHANGES, AND BOTH ARE THE SAME IDEA:
                THE SCREEN MUST NOT OFFER AN ACTION IT CANNOT PERFORM.
                (a) THE LOCAL WITNESS COUNTS, not only the ledger (security Med2).
                `latestSend` arrives through a second read that can FAIL, and on a
                re-send it can be a stale row describing send #1 — so a successful
                send that the refresh could not confirm left a gold
                'Send quote to customer' on screen, one tap from minting a second
                deposit link. `sentOk` is this component's own witness of the 2xx
                and it takes the same post-send state the ledger row would have
                given the button. The hosts ask the same question through
                `sendQuoteHasPrimary`, so the screen's one gold moves in step.
                (b) NOTHING CAN CARRY IT → §5.1's DISABLED TREATMENT, through
                `Button`, which withholds `onClick` AND cancels the keyboard
                default (an `aria-disabled` that still acts is the failure that
                rule exists for). The reason is `aria-describedby`, rendered once
                below. ⚠️ `Button` rather than the hand-rolled `<button>` for
                exactly that: the suppression and the dashed `--ink-600` edge are
                the library's, and a screen may not draw a state it does not own. */}
            <Button
              variant={sent || yieldGold ? 'quiet' : 'primary'}
              disabled={busy}
              ariaDisabled={nothingCanCarry ? true : undefined}
              ariaDescribedBy={nothingCanCarry ? NOTHING_CAN_CARRY_ID : undefined}
              onClick={() => void send()}
            >
              {sent ? 'Send again' : 'Send quote to customer'}
            </Button>
          </div>
          {/* ⛔ ONCE, UNDER THE BLOCK'S ONE ACTION, and never per channel: the
              off lines above say WHICH transports are off, and this says what
              that means for the send. It is the description the refusing primary
              points at, so a reader hears the name, then "dimmed", then this. */}
        </>
      )}

      {failure && (
        <Banner kind={failure.kind === 'not-configured' ? 'info' : 'error'}>
          {failure.message}{' '}
          {failure.kind !== 'not-configured' && (
            <button type="button" class="btn btn--quiet btn--inline" disabled={busy} onClick={() => void send()}>
              Try again
            </button>
          )}
        </Banner>
      )}

      {/* GATED ON THE PHONE, both buttons together, and this is the one place
          the two hosts disagreed before T660 M2 - so unifying them had to pick a
          side rather than average them.
            - BookingDetail mounted the pair inside the phone value cell, so a
              customer with no number on file got neither.
            - Done mounted them beside `customerName || customerPhone` and gated
              only the NUMBER button on the phone, so a phone-less booking showed
              a "Copy message" that could not work: `POST .../copy-quote` refuses
              409 `no_phone` with no inquiry-side fallback (app.ts:3872-3876),
              exactly as the SMS send arm does.
          BookingDetail's rule wins because it is the server's. A button whose
          only possible outcome is a refusal is worse than no button on a screen
          whose hard constraint is "no extra buttons", and it withholds nothing
          real: there is no route by which that message can be composed.

          GROUP 2 of 2 (P0-1) — the MANUAL routes. Its own container and its own
          name, deliberately not a third radio: these are not another way for the
          system to send, they are what Helen does by hand for a customer no send
          can reach. "Or copy it" is the whole reason the label is visible - the
          buttons moved off the phone line to get here, and without the label
          they would read as part of the send flow above.

          P0-2 lives here too, by construction: WhatsApp is a copy destination
          and can only ever be one (there is no customer-send driver, and the API
          answers 400 for `whatsapp`), so it can appear in THIS group and never
          in the radiogroup. `offered` is filtered from IMPLEMENTED_CHANNELS,
          which does not contain it.

          T660 M3 (P0-3) puts the PAYMENT LINK row in this same group - the third
          copy row, not a third group - and widens the group's gate by exactly one
          disjunct to do it. The `copyPhone !== null` half is unchanged and still
          gates the two buttons that need a phone; the new row does not, because
          the reason those two are phone-gated is a server rule they alone are
          subject to (`copy-quote` refuses 409 `no_phone`). Leaving the money path
          under a gate whose stated reason does not apply to it would withhold the
          only working delivery from an email-only customer for no reason anyone
          could find later. */}
      {(copyPhone !== null || onCopyPaymentLink !== null) && (
        <fieldset class="delivery-group">
          <legend class="delivery-group-label">Or copy it</legend>
          {/* THE GROUP SAYS WHAT IT DOES NOT DO, in its own words (M3 review).
              The "Send it" group states its consequence plainly one line above
              ("Goes to the customer at ... Not the team."); this group stated
              none, so "nothing is sent" rested entirely on the word "only"
              inside ONE of three button labels - and that button breaks the
              verb-first pattern the other two set, so a hurried reading parses
              "Payment link only" as a scope filter rather than as "this copies,
              and sends nothing".

              ONE sentence, and the same muted `.recipient-copy` caption the
              send group's line uses, so it reads as the group's consequence
              rather than as another control. The screen's hard constraint is
              "too many buttons"; a line that says what will NOT happen is the
              cheapest thing on it.

              INSIDE the fieldset and AFTER the legend, never in it: HTML-AAM
              names a fieldset from its legend alone, so the group's accessible
              name stays exactly 'Or copy it' (queried by that name in the e2e
              net and in three specs). No new CSS, and no new string that is a
              substring of any accessible name on this screen. */}
          <p class="recipient-copy">These go to your clipboard, and nothing is sent.</p>
          <div class="copy-choice">
            <CopyAffordances
              phone={copyPhone}
              /* ⚠️ THE PHONE RULE FOR THE MESSAGE BUTTON MOVED HERE, and it had
                 to. Until T660 M3 it was enforced by the group's gate as a side
                 effect: no phone, no group, so no message button. Widening that
                 gate for the payment-link row would have let the message button
                 through on a phone-less booking, where `POST .../copy-quote`
                 refuses 409 `no_phone` with no inquiry-side fallback
                 (app.ts:3872-3876) - a button whose only possible outcome is a
                 refusal, which is exactly what M2 removed.

                 Stated as a conjunct rather than pushed into CopyAffordances,
                 because that component withholds each button independently on
                 its own input by design (its spec pins a phone-less render still
                 offering Copy message) and BOTH gates belong at the call site. */
              onCopyMessage={copyPhone !== null ? onCopyMessage : null}
              onCopyPaymentLink={onCopyPaymentLink}
            />
          </div>
        </fieldset>
      )}

      {sentOk && contactReadFailed ? (
        /* ⭐⭐ DS-20 b, ROUND 2 (security Med2) — THE WITNESS OUTRANKS A STALE
           LEDGER ROW, and the ORDER of these two arms is the whole fix. It used to
           test `latestSend` first, so a RE-send whose refresh failed rendered send
           #1's sentence — a confident, wrong statement about the send Helen just
           made. The witness is checked first: the send landed and the read after
           it did not, whatever row is still in the store.
           No chip: there is no ledger row for THIS send, and inventing a status
           would be the optimistic 'sent' SEC-7 forbids. */
        <p class="send-status">{SENT_UNREFRESHED_COPY}</p>
      ) : latestSend ? (
        <p class="send-status">
          {/* ⭐ T1749 M8 (DS-20 a): the STATE is the first words, off the ledger,
              in one sentence — see `sentSentence` for the three arms and for why
              the channel and the recipient ride all three. The chip keeps the
              TRUE ledger status through the registry label, never an optimistic
              'sent' (SEC-7), and keeps its tone (board 1962).

              TEXT, not a control's accessible name - so it needs no
              `data-fb-opaque`. That marker exists because `describeTarget`
              records the name of every control a reviewer clicks and
              `redactSensitive` never catches names; a paragraph is not
              clicked. The value is the same class of data this block already
              renders one line up ("Goes to the customer at ..."), so it adds no
              new exposure to the screenshot path. */}
          {sentSentence(latestSend, channelLabel)}{' '}
          <span class={statusChipClass(latestSend.deliveryStatus, latestSend.channel)}>
            {sendStatusLabel(latestSend.deliveryStatus)}
          </span>
        </p>
      ) : null}
    </div>
  );
}
